import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { lifecycleClass, lifecycleLabel, lifecycleTitle } from "@/lib/lifecycleStage";

import DeprecationBanner from "./DeprecationBanner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ── Recommendation Type Badge ──
const TypeBadge = ({ type }) => {
  const styles = {
    TEST: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    SCALE: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    RETIRE: "bg-red-500/15 text-red-400 border-red-500/30",
  };
  return (
    <span
      data-testid={`rec-type-badge-${type}`}
      className={`px-2.5 py-0.5 rounded text-xs font-semibold border ${styles[type] || "bg-slate-700 text-slate-300 border-slate-600"}`}
    >
      {type}
    </span>
  );
};

// ── Decision Badge ──
const DecisionBadge = ({ decision }) => {
  const styles = {
    PENDING: "bg-amber-500/15 text-amber-400",
    ACCEPTED: "bg-emerald-500/15 text-emerald-400",
    OVERRIDDEN: "bg-purple-500/15 text-purple-400",
    IGNORED: "bg-slate-700 text-slate-400",
  };
  return (
    <span
      data-testid={`rec-decision-badge-${decision}`}
      className={`px-2 py-0.5 rounded text-xs font-medium ${styles[decision] || "bg-slate-700 text-slate-400"}`}
    >
      {decision || "PENDING"}
    </span>
  );
};

// ── Impact Score Bar ──
const ImpactBar = ({ score, maxScore = 100 }) => {
  const pct = Math.min((score / maxScore) * 100, 100);
  const color = pct > 70 ? "bg-emerald-500" : pct > 40 ? "bg-amber-500" : "bg-slate-500";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-mono text-slate-400 w-8 text-right">{score.toFixed(1)}</span>
    </div>
  );
};

// ── Decision Modal ──
const DecisionModal = ({ rec, onClose, onDecide }) => {
  const [decision, setDecision] = useState("");
  const [reasoning, setReasoning] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!decision) return;
    setLoading(true);
    try {
      await onDecide(rec.recommendation_id, decision, reasoning);
      onClose();
    } catch {
      alert("Failed to record decision");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-slate-900 border border-slate-700 rounded-xl max-w-lg w-full p-6 space-y-5"
        onClick={(e) => e.stopPropagation()}
        data-testid="decision-modal"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">Record Decision</h3>
          <TypeBadge type={rec.recommendation_type} />
        </div>

        <p className="text-slate-300 text-sm leading-relaxed">{rec.recommendation_text}</p>

        {rec.projected_outcome && (
          <div className="bg-slate-800/60 rounded-lg p-3 space-y-1">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Projected Outcome</p>
            <p className="text-sm text-slate-300">
              Impact Score: <span className="text-emerald-400 font-semibold">{rec.impact_score?.toFixed(2)}</span>
            </p>
            {rec.projected_outcome.estimated_daily_profit_change !== undefined && (
              <p className="text-sm text-slate-300">
                Est. Daily Profit Change: <span className="text-emerald-400 font-semibold">
                  ${rec.projected_outcome.estimated_daily_profit_change?.toFixed(2)}
                </span>
              </p>
            )}
          </div>
        )}

        <div className="space-y-3">
          <p className="text-sm text-slate-400">Your decision:</p>
          <div className="flex gap-2">
            {["ACCEPTED", "OVERRIDDEN", "IGNORED"].map((d) => (
              <button
                key={d}
                onClick={() => setDecision(d)}
                data-testid={`decision-btn-${d.toLowerCase()}`}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  decision === d
                    ? d === "ACCEPTED"
                      ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-400"
                      : d === "OVERRIDDEN"
                      ? "bg-purple-500/20 border-purple-500/50 text-purple-400"
                      : "bg-slate-700 border-slate-500 text-slate-300"
                    : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"
                }`}
              >
                {d}
              </button>
            ))}
          </div>

          <textarea
            value={reasoning}
            onChange={(e) => setReasoning(e.target.value)}
            placeholder="Reasoning (optional)..."
            data-testid="decision-reasoning-input"
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 resize-none h-20"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            data-testid="decision-cancel-btn"
            className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!decision || loading}
            data-testid="decision-submit-btn"
            className="px-5 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Saving..." : "Confirm Decision"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Mutation Lineage Viewer ──
const LineageViewer = ({ creativeId, onClose }) => {
  const [lineage, setLineage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!creativeId) return;
    axios
      .get(`${API}/mutation/lineage/${creativeId}`)
      .then((r) => setLineage(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [creativeId]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center">
        <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-slate-900 border border-slate-700 rounded-xl max-w-2xl w-full p-6 max-h-[80vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="lineage-viewer-modal"
      >
        <h3 className="text-lg font-bold text-white mb-4">Mutation Lineage</h3>

        {lineage && (
          <div className="space-y-4">
            <div className="bg-slate-800/60 rounded-lg p-3 space-y-2">
              <p className="text-sm text-slate-400">
                Mode: <span className="text-white font-medium">{lineage.generation_mode || "N/A"}</span>
              </p>
              <p className="text-sm text-slate-400">
                Stage:{" "}
                <span
                  className={lifecycleClass(lineage.lifecycle_stage)}
                  title={lifecycleTitle(lineage.lifecycle_stage)}
                >
                  {lifecycleLabel(lineage.lifecycle_stage)}
                </span>
              </p>
              <p className="text-sm text-slate-400">
                Depth: <span className="text-white font-medium">{lineage.generation_depth || 0}</span>
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Ancestor Chain</p>
              {lineage.lineage?.length > 0 ? (
                lineage.lineage.map((ancestor, idx) => (
                  <div key={ancestor.creative_id || idx} className="flex items-center gap-3 p-2 bg-slate-800/40 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-xs text-slate-400 flex-shrink-0">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{ancestor.creative_name || ancestor.creative_id}</p>
                      <p className="text-xs text-slate-500">
                        {ancestor.generation_mode} ·{" "}
                        <span
                          className={lifecycleClass(ancestor.lifecycle_stage, "")}
                          title={lifecycleTitle(ancestor.lifecycle_stage)}
                        >
                          {lifecycleLabel(ancestor.lifecycle_stage)}
                        </span>
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500 italic">No ancestors (root creative)</p>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end pt-4">
          <button onClick={onClose} data-testid="close-lineage-btn" className="px-4 py-2 text-sm text-slate-400 hover:text-white">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN RECOMMENDATIONS DASHBOARD
// ============================================================================
const RecommendationsDashboard = () => {
  const [recs, setRecs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const [decisionFilter, setDecisionFilter] = useState("PENDING");
  const [selectedRec, setSelectedRec] = useState(null);
  const [lineageCreativeId, setLineageCreativeId] = useState(null);
  const [generating, setGenerating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (filter !== "ALL") params.set("rec_type", filter);
      if (decisionFilter !== "ALL") params.set("decision", decisionFilter);

      const [recsRes, sumRes] = await Promise.all([
        axios.get(`${API}/recommendations?${params}`),
        axios.get(`${API}/recommendations/summary`),
      ]);
      setRecs(recsRes.data.recommendations || []);
      setSummary(sumRes.data);
    } catch (err) {
      console.error("Failed to fetch recommendations:", err);
    } finally {
      setLoading(false);
    }
  }, [filter, decisionFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/recommendations/generate`);
      await fetchData();
    } catch (err) {
      alert("Failed to generate recommendations: " + (err.response?.data?.detail || err.message));
    }
    setGenerating(false);
  };

  const handleDecide = async (recId, decision, reasoning) => {
    await axios.post(`${API}/recommendations/${recId}/decide`, { decision, reasoning });
    await fetchData();
  };

  const maxImpact = recs.length > 0 ? Math.max(...recs.map((r) => r.impact_score || 0), 1) : 1;

  return (
    <div className="space-y-6" data-testid="recommendations-dashboard">
      <DeprecationBanner surface="The Recommendations dashboard" />
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white" data-testid="recommendations-title">
            Recommendations
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Prescriptive actions ranked by estimated impact
          </p>
        </div>
        <button
          onClick={handleGenerate}
          disabled={generating}
          data-testid="generate-recommendations-btn"
          className="px-5 py-2.5 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 disabled:opacity-50 transition-colors text-sm"
        >
          {generating ? "Generating..." : "Generate Recommendations"}
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="stat-card-hero" data-testid="summary-total">
            <p className="stat-number text-white">{summary.total}</p>
            <p className="stat-label text-slate-400">Total</p>
          </div>
          <div className="stat-card-hero" data-testid="summary-pending">
            <p className="stat-number text-purple-400">{summary.pending}</p>
            <p className="stat-label text-slate-400">Pending</p>
          </div>
          <div className="stat-card-hero" data-testid="summary-accepted">
            <p className="stat-number text-emerald-400">{summary.accepted}</p>
            <p className="stat-label text-slate-400">Accepted</p>
          </div>
          <div className="stat-card-hero" data-testid="summary-conversion">
            <p className="stat-number text-blue-400">
              {summary.total > 0 ? ((summary.accepted / summary.total) * 100).toFixed(0) : 0}%
            </p>
            <p className="stat-label text-slate-400">Acceptance Rate</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex gap-1 bg-slate-900/60 border border-slate-800 rounded-lg p-1">
          {["ALL", "TEST", "SCALE", "RETIRE"].map((t) => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              data-testid={`filter-type-${t.toLowerCase()}`}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                filter === t
                  ? "bg-emerald-500/20 text-emerald-400"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="flex gap-1 bg-slate-900/60 border border-slate-800 rounded-lg p-1">
          {["ALL", "PENDING", "ACCEPTED", "OVERRIDDEN", "IGNORED"].map((d) => (
            <button
              key={d}
              onClick={() => setDecisionFilter(d)}
              data-testid={`filter-decision-${d.toLowerCase()}`}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                decisionFilter === d
                  ? "bg-emerald-500/20 text-emerald-400"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              {d}
            </button>
          ))}
        </div>
      </div>

      {/* Recommendations List */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
        </div>
      ) : recs.length === 0 ? (
        <div className="text-center py-16 bg-slate-900/40 border border-slate-800 rounded-xl" data-testid="no-recommendations">
          <p className="text-slate-500 text-sm">No recommendations found. Click Generate to create new ones.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {recs.map((rec) => (
            <div
              key={rec.recommendation_id}
              data-testid={`rec-card-${rec.recommendation_id}`}
              className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 hover:border-slate-700 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <TypeBadge type={rec.recommendation_type} />
                    <DecisionBadge decision={rec.decision} />
                    {rec.time_to_action && (
                      <span className="text-xs text-slate-500">
                        Action: {rec.time_to_action}
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-slate-200 leading-relaxed">
                    {rec.recommendation_text || "No description"}
                  </p>

                  <div className="flex items-center gap-6 text-xs text-slate-500">
                    {rec.related_offer_id && <span>Offer: {rec.related_offer_id.slice(0, 12)}</span>}
                    {rec.related_campaign_id && <span>Campaign: {rec.related_campaign_id.slice(0, 12)}</span>}
                    {rec.surfaced_date && <span>Surfaced: {new Date(rec.surfaced_date).toLocaleDateString()}</span>}
                  </div>

                  {/* Impact Score */}
                  <div className="max-w-xs">
                    <ImpactBar score={rec.impact_score || 0} maxScore={maxImpact} />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-2 flex-shrink-0">
                  {rec.decision === "PENDING" && (
                    <button
                      onClick={() => setSelectedRec(rec)}
                      data-testid={`decide-btn-${rec.recommendation_id}`}
                      className="px-3 py-1.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded-lg text-xs font-medium hover:bg-emerald-500/20 transition-colors"
                    >
                      Decide
                    </button>
                  )}
                  {rec.execution_package?.parent_creative_id && (
                    <button
                      onClick={() => setLineageCreativeId(rec.execution_package.parent_creative_id)}
                      data-testid={`lineage-btn-${rec.recommendation_id}`}
                      className="px-3 py-1.5 bg-slate-800 text-slate-400 border border-slate-700 rounded-lg text-xs font-medium hover:text-white transition-colors"
                    >
                      Lineage
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Decision Modal */}
      {selectedRec && (
        <DecisionModal
          rec={selectedRec}
          onClose={() => setSelectedRec(null)}
          onDecide={handleDecide}
        />
      )}

      {/* Lineage Viewer */}
      {lineageCreativeId && (
        <LineageViewer
          creativeId={lineageCreativeId}
          onClose={() => setLineageCreativeId(null)}
        />
      )}
    </div>
  );
};

export default RecommendationsDashboard;
